import React, { useState, useEffect } from 'react';
import { Box, Typography, Tabs, Tab, Container } from '@mui/material';
import ExecutorMainInfoDesktop from '../../main-info/desktop/ExecutorMainInfoDesktop'; 
import styles from './ExecutorDashboardPage.module.css'; 

import NoTasksPage from '../../my-tasks/desktop/NoTasksPage';
import TaskList from '../../my-tasks/desktop/TaskList';
import { taskService } from '../../../../services/taskService';
import { Task } from '../../../../types/task';
import { TaskStatus } from '../../../../types/task-status';

// @ts-ignore
import mainInfoIcon from './img/MainInfo.png';
// @ts-ignore
import mainInfoActiveIcon from './img/MainInfoActive.png';
// @ts-ignore
import myTaskIcon from './img/MyTask.png';
// @ts-ignore
import myTaskActiveIcon from './img/MyTaskActive.png';
// @ts-ignore
import resumeIcon from './img/Rez.png';
// @ts-ignore
import resumeActiveIcon from './img/RezActive.png';
// @ts-ignore
import docAndFinIcon from './img/DocAndFin.png';
// @ts-ignore
import docAndFinActiveIcon from './img/DocAndFinActive.png';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box>
                    {children}
                </Box>
            )}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

const ExecutorDashboardPage: React.FC = () => {
    const [value, setValue] = useState(0);
    const [tasks, setTasks] = useState<Task[]>([]);
    const [loadingTasks, setLoadingTasks] = useState(true);
    const [errorTasks, setErrorTasks] = useState<string | null>(null);
    const [selectedStatusFilter, setSelectedStatusFilter] = useState<TaskStatus | undefined>(undefined);
    const [newestFirst, setNewestFirst] = useState<boolean>(true);
    const [allTasks, setAllTasks] = useState<Task[]>([]); // To store all tasks, unfiltered

    useEffect(() => {
        console.log('ExecutorDashboardPage: useEffect triggered for task fetching');
        console.log('ExecutorDashboardPage: selectedStatusFilter', selectedStatusFilter);
        console.log('ExecutorDashboardPage: newestFirst', newestFirst);
        const fetchTasks = async () => {
            try {
                setLoadingTasks(true);
                const allFetchedTasks = await taskService.getMyTasks(undefined, true); // Fetch all tasks initially, default sort
                setAllTasks(allFetchedTasks);
                console.log('ExecutorDashboardPage: All tasks fetched', allFetchedTasks);
                // Apply filter and sort locally
                let filteredTasks = selectedStatusFilter
                    ? allFetchedTasks.filter(task => task.status === selectedStatusFilter)
                    : allFetchedTasks;
                console.log('ExecutorDashboardPage: Filtered tasks', filteredTasks);
                filteredTasks = newestFirst
                    ? [...filteredTasks].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                    : [...filteredTasks].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
                setTasks(filteredTasks);
                console.log('ExecutorDashboardPage: Sorted tasks', filteredTasks);
            } catch (err) {
                setErrorTasks('Failed to fetch tasks.');
                console.error('ExecutorDashboardPage: Error fetching tasks', err);
            } finally {
                setLoadingTasks(false);
                console.log('ExecutorDashboardPage: Task fetch finished');
            }
        };
        fetchTasks();
    }, [selectedStatusFilter, newestFirst]); // Rerun when filters or sort change

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        console.log('ExecutorDashboardPage: Tab changed to', newValue);
        setValue(newValue);
    };

    const handleStatusChange = (status: TaskStatus | undefined, sortNewestFirst: boolean) => {
        setSelectedStatusFilter(status);
        setNewestFirst(sortNewestFirst);
    };

    // Функция для обновления одной задачи
    const handleTaskUpdate = (updatedTask: Task) => {
        console.log('ExecutorDashboardPage: Task updated locally', updatedTask);
        // Обновляем allTasks
        setAllTasks(prevAllTasks => 
            prevAllTasks.map(task => task.id === updatedTask.id ? updatedTask : task)
        );

        // Обновляем filteredTasks (tasks)
        setTasks(prevTasks => {
            const updatedFilteredTasks = prevTasks.map(task => 
                task.id === updatedTask.id ? updatedTask : task
            );
            // Применяем фильтр к обновленному списку на случай, если статус задачи изменился так, что она больше не соответствует текущему фильтру
            return selectedStatusFilter
                ? updatedFilteredTasks.filter(task => task.status === selectedStatusFilter)
                : updatedFilteredTasks;
        });
    };

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }} className={styles.dashboardContainer}>
            <Typography variant="h4" gutterBottom>Это твой личный профиль на EAZZY:</Typography>
            <Box sx={{ width: '100%', bgcolor: 'background.paper' }} className={styles.tabsContainer}>
                <Tabs value={value} onChange={handleChange} aria-label="Executor dashboard tabs">
                    <Tab label="Основная информация" {...a11yProps(0)} icon={<img src={value === 0 ? mainInfoActiveIcon : mainInfoIcon} alt="Основная информация" className={styles.tabIcon} />} iconPosition="start" />
                    <Tab label="Мои задания" {...a11yProps(1)} icon={<img src={value === 1 ? myTaskActiveIcon : myTaskIcon} alt="Мои задания" className={styles.tabIcon} />} iconPosition="start" />
                    <Tab label="Резюме" {...a11yProps(2)} icon={<img src={value === 2 ? resumeActiveIcon : resumeIcon} alt="Резюме" className={styles.tabIcon} />} iconPosition="start" />
                    <Tab label="Документы и финансы" {...a11yProps(3)} icon={<img src={value === 3 ? docAndFinActiveIcon : docAndFinIcon} alt="Документы и финансы" className={styles.tabIcon} />} iconPosition="start" />
                </Tabs>
            </Box>
            <CustomTabPanel value={value} index={0}>
                <ExecutorMainInfoDesktop />
            </CustomTabPanel>
            <CustomTabPanel value={value} index={1}>
                {loadingTasks ? (
                    <Typography>Загрузка задач...</Typography>
                ) : errorTasks ? (
                    <Typography color="error">Ошибка: {errorTasks}</Typography>
                ) : allTasks.length === 0 ? (
                    <NoTasksPage />
                ) : (
                    <TaskList 
                        tasks={tasks} 
                        onStatusChange={handleStatusChange} 
                        onTaskUpdate={handleTaskUpdate} // Передаем новую функцию
                        selectedStatus={selectedStatusFilter} 
                        newestFirst={newestFirst} 
                    />
                )}
            </CustomTabPanel>
            <CustomTabPanel value={value} index={2}>
                <Typography>Страница "Резюме"</Typography>
            </CustomTabPanel>
            <CustomTabPanel value={value} index={3}>
                <Typography>Страница "Документы и финансы"</Typography>
            </CustomTabPanel>
        </Container>
    );
};

export default ExecutorDashboardPage;
